import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Divider, Dropdown, Menu, message, Space, Tooltip, Select, Badge } from 'antd'
import {
  DeleteModal,
  FilterForm,
  IconFont,
  OptionType,
  SvgIcon,
  PageContainer,
  FilterFormSelect,
} from '@/components'
import {
  useDeleteTask,
  useDeleteList,
  useGetBizAreaList,
  useGetGroupList,
  useGetTaskExecList,
  useGetTaskList,
  useStopTask,
  useGetSecurityDeviceList,
  useGetAgentList,
  useChangeJobStatus,
} from './utils'
import TaskTable from '@/components/task-table/task-table'
import DetailDrawer from './detail-drawer'
import { RetestModal } from './retest-modal'
import { useNavigate } from 'react-router'
import { CreateReport } from '@/components/create-report'
import { useAddReport } from '@/pages/evaluation/report-task/utils'
import { Message } from '@/components/message'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import { formatDateTime } from '@/utils/date'
import { t } from '@/i18n'

export interface ESDataType {
  //  执行次数上限
  executeTimesLimit: number
  // 已执行次数
  executeTimesUsed: number
  // 首次运行时间
  firstExecuteTime: string
  // 任务分组
  groupName: string
  // 任务id
  id: string
  // 执行类型
  kind: KindProps
  // 最近结束时间
  latestExecuteTime: string
  // 任务名称
  name: string
  // 任务综合评分
  score: number
  // 状态分布
  statusList: { status: string; count: number }[]
  // 验证类型
  verificationType: string
  // 子任务id
  subRowKeys: string[]
}

export interface ESSubDataType {
  //  客户端
  agent: string
  //  安全域
  areaName: string
  //  用例发送数
  caseSendTotal: number
  //  用例总数
  caseTotal: number
  //  创建时间
  createTime: string
  // 开始时间
  startTime: string
  //  结束时间
  endTime: string
  //  任务执行id
  id: string
  //  任务评分
  score: number
  // 执行状态
  status: string
  // 离线验证任务名称
  taskName?: string
  // 是否可以删除
  isRemove: boolean
}

type KindProps = 'TaskKindImmediately' | 'TaskKindBooking' | 'TaskKindCycle'

const KindMap = {
  TaskKindImmediately: t('立即'),
  TaskKindBooking: t('预定'),
  TaskKindCycle: t('周期'),
}

const VerificationTypeMap = {
  VerificationTypeOnline: { label: t('在线验证'), color: '#67C23A' },
  VerificationTypeOffline: { label: t('离线验证'), color: '#999999' },
}

const statusMap = {
  TaskStatusFinished: { label: t('已完成'), className: 'success' },
  TaskStatusRunning: { label: t('进行中'), className: 'processing' },
  TaskStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskStatusError: { label: t('错误'), className: 'error' },
  TaskStatusStopping: { label: t('停止中'), className: 'warning' },
  TaskStatusPause: { label: t('暂停'), className: 'warning' },
}

const statusResultMap = {
  TaskResultStatusFinished: { label: t('已完成'), className: 'success' },
  TaskResultStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskResultStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskResultStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskResultStatusStopping: { label: t('停止中'), className: 'warning' },
}

// 计算评分
const calculateScore = (itemScore: number, status: string): number | string => {
  if (itemScore === 0) {
    return status !== 'TaskResultStatusFinished' ? '-' : 0
  }
  if (itemScore === 100) return 100
  return Number(itemScore.toFixed(2)) || 0
}

// 计算风险等级
const calculateRiskLevel = (
  score: number | string,
  status: string,
): { riskLevel: string; className: string } => {
  if (status !== 'TaskResultStatusFinished') {
    return { riskLevel: '-', className: '' }
  }

  if (typeof score === 'string') {
    return { riskLevel: '-', className: '' }
  }

  if (score <= 10000 && score >= 8000) return { riskLevel: t('优秀'), className: 'low-risk' }
  if (score < 8000 && score >= 6000) return { riskLevel: t('良好'), className: 'medium-risk' }
  if (score < 6000 && score >= 4000) return { riskLevel: t('一般'), className: 'high-risk' }
  if (score < 4000 && score >= 2000) return { riskLevel: t('较弱'), className: 'weaker-risk' }
  return { riskLevel: t('弱'), className: 'serious-risk' }
}

// 检查状态是否在指定列表中
const isStatusInList = (status: string, statusList: string[]): boolean =>
  statusList.includes(status)

// 计算权限标志
const calculatePermissions = (status: string, caseSendTotal: number) => {
  const waitingProcessingStopping = [
    'TaskResultStatusWaiting',
    'TaskResultStatusProcessing',
    'TaskResultStatusStopping',
  ]

  const processingStoppingInterrupted = [
    'TaskResultStatusProcessing',
    'TaskResultStatusStopping',
    'TaskResultStatusInterrupted',
  ]

  const waitingProcessingInterruptedStopping = [
    'TaskResultStatusWaiting',
    'TaskResultStatusProcessing',
    'TaskResultStatusInterrupted',
    'TaskResultStatusStopping',
  ]

  const isRetest = !(
    isStatusInList(status, waitingProcessingStopping) ||
    (isStatusInList(status, processingStoppingInterrupted) && !caseSendTotal)
  )

  const isReport = !(
    isStatusInList(status, waitingProcessingStopping) ||
    (isStatusInList(status, processingStoppingInterrupted) && !caseSendTotal)
  )

  const isRemove = !isStatusInList(status, waitingProcessingStopping)

  const isView = !(isStatusInList(status, waitingProcessingInterruptedStopping) && !caseSendTotal)

  const isStop = isStatusInList(status, ['TaskResultStatusWaiting', 'TaskResultStatusProcessing'])

  return { isRetest, isReport, isRemove, isView, isStop }
}

// 处理单个子任务项
const processSubTaskItem = (item: any, taskId: string) => {
  const { id, taskName, status, caseSendTotal, abnormalReason, createTime, startTime, endTime } =
    item

  const score = calculateScore(item.score, status)
  const { riskLevel, className } = calculateRiskLevel(score, status)
  const { isRetest, isReport, isRemove, isView, isStop } = calculatePermissions(
    status,
    caseSendTotal,
  )

  return {
    ...item,
    key: id,
    createTime: createTime || '-',
    startTime: startTime || '-',
    endTime: endTime || '-',
    taskId,
    taskName,
    score: { score, riskLevel, className, status },
    status: statusResultMap[status],
    isRetest,
    isReport,
    isRemove,
    isView,
    isStop,
    exceptReason: abnormalReason || undefined,
  }
}

// 构建子任务数据
const buildSubDataSource = (data, taskId: string) => {
  if (!data || data.length === 0) return []

  const arr = JSON.parse(JSON.stringify(data))
  return arr.map(item => processSubTaskItem(item, taskId))
}

// 使用自定义Hook管理状态
const useTaskListState = () => {
  const navigate = useNavigate()
  const [area, getArea] = useGetBizAreaList()
  const [getGroupList, groupList] = useGetGroupList()
  const [changeJobStatus] = useChangeJobStatus()
  const [getTaskList, getTaskListLoading] = useGetTaskList()
  const [getTaskExecuteList, getTaskExecuteListLoading] = useGetTaskExecList()
  const [stopTask] = useStopTask()
  const [deleteTask, deleteTaskLoading] = useDeleteTask()
  const [deleteSubTask, deleteSubTaskLoading] = useDeleteList()
  const [createReport, createLoading] = useAddReport()
  const [getSecurityDeviceList, securityDeviceList] = useGetSecurityDeviceList()
  const [getAgentList, agentList] = useGetAgentList()

  const [filterParams, setFilterParams] = useState({ page: 1, limit: 10 })
  const [searchParams, setSearchParams] = useState({ page: 1, limit: 10 })
  const [total, setTotal] = useState(0)
  const [dataSource, setDataSource] = useState<ESDataType[]>([])
  const [subDataSource, setSubDataSource] = useState<ESSubDataType[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedSubRowKeys, setSelectedSubRowKeys] = useState<string[]>([])
  const [count, setCount] = useState(1)
  const [subTotal, setSubTotal] = useState(0)
  const [view, setView] = useState<{ visible: boolean; id: string }>({ visible: false, id: '' })
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [retestVisible, setRetestVisible] = useState({ visible: false, result_id: '', task_id: '' })
  const [remove, setRemove] = useState<{ visible: boolean; id: string[]; type: string }>({
    visible: false,
    id: [],
    type: '',
  })
  const [report, setReport] = useState<{
    visible: boolean
    id: string | string[]
    name: string
    multiple?: boolean
    taskList?: { task_name: string; campaign_id: string }[]
    selectItem?: any
  }>({ visible: false, id: '', multiple: false, name: '', taskList: [], selectItem: null })
  // 任务筛选
  const [taskFormValue, setTaskFormValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })
  const [mainTaskName, setMainTaskName] = useState('')
  const [groupSelectOpen, setGroupSelectOpen] = useState(false)

  return {
    navigate,
    area,
    getArea,
    getSecurityDeviceList,
    securityDeviceList,
    getAgentList,
    agentList,
    getGroupList,
    groupList,
    changeJobStatus,
    getTaskList,
    getTaskListLoading,
    getTaskExecuteList,
    getTaskExecuteListLoading,
    stopTask,
    deleteTask,
    deleteTaskLoading,
    deleteSubTask,
    deleteSubTaskLoading,
    createReport,
    createLoading,
    filterParams,
    setFilterParams,
    searchParams,
    setSearchParams,
    total,
    setTotal,
    dataSource,
    setDataSource,
    subDataSource,
    setSubDataSource,
    expandedRowKeys,
    setExpandedRowKeys,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedSubRowKeys,
    setSelectedSubRowKeys,
    count,
    setCount,
    subTotal,
    setSubTotal,
    view,
    setView,
    importModalVisible,
    setImportModalVisible,
    retestVisible,
    setRetestVisible,
    remove,
    setRemove,
    report,
    setReport,
    mainTaskName,
    setMainTaskName,
    groupSelectOpen,
    setGroupSelectOpen,
    taskFormValue,
    setTaskFormValue,
  }
}

const TaskListMain: React.FC = () => {
  const {
    navigate,
    area,
    getArea,
    getSecurityDeviceList,
    securityDeviceList,
    getAgentList,
    agentList,
    getGroupList,
    groupList,
    changeJobStatus,
    getTaskList,
    getTaskListLoading,
    getTaskExecuteList,
    getTaskExecuteListLoading,
    stopTask,
    deleteTask,
    deleteTaskLoading,
    deleteSubTask,
    deleteSubTaskLoading,
    createReport,
    createLoading,
    filterParams,
    setFilterParams,
    searchParams,
    setSearchParams,
    total,
    setTotal,
    dataSource,
    setDataSource,
    subDataSource,
    setSubDataSource,
    expandedRowKeys,
    setExpandedRowKeys,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedSubRowKeys,
    setSelectedSubRowKeys,
    count,
    setCount,
    subTotal,
    setSubTotal,
    view,
    setView,
    importModalVisible,
    setImportModalVisible,
    retestVisible,
    setRetestVisible,
    remove,
    setRemove,
    report,
    setReport,
    mainTaskName,
    setMainTaskName,
    groupSelectOpen,
    setGroupSelectOpen,
    taskFormValue,
    setTaskFormValue,
  } = useTaskListState()

  const options = [
    {
      label: t('主任务名称'),
      value: 'taskName',
      type: 'input' as const,
    },
    {
      label: t('子任务名称'),
      value: 'resultName',
      type: 'input' as const,
    },
    {
      label: t('执行主体'),
      value: 'agentId',
      type: 'select' as const,
      options: agentList.map(item => ({
        label: `${item.agentName} | ${item.agentIp}`,
        value: `${item.agentId}&&${item.agentName}&&${item.agentIp}`,
      })),
    },
    {
      label: t('安全设备'),
      value: 'deviceIds',
      type: 'select' as const,
      mode: 'multiple',
      options: securityDeviceList.map(item => ({
        label: `${item.name || '-'}  | ${item.deviceType} | ${item.deviceIp}`,
        value: item.id,
      })),
    },
    {
      label: t('安全域'),
      value: 'areaName',
      type: 'select' as const,
      options: area.map(item => ({
        label: item.name,
        value: item.name,
      })),
    },
  ]

  const buildItems = ({ label }, id, name, isRemove) => {
    const items = [
      {
        key: 'copy',
        label: (
          <div className="copy-btn" onClick={() => copyData(id)}>
            <span className="text">{t('任务复制')}</span>
          </div>
        ),
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'main')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),
      },
    ]

    return <Menu items={items} />
  }

  const buildSubItems = (
    taskName: string,
    id: string,
    taskId: string,
    status: string,
    isRetest: boolean,
    isReport: boolean,
    isRemove: boolean,
  ) => {
    const items = [
      // 报表权限
      {
        key: 'report',
        label: (
          <div
            className={isReport ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => {
              if (!isReport) return
              reportData(id, taskName)
            }}
          >
            <span className="text">{t('生成报表')}</span>
          </div>
        ),

        disabled: !isReport,
      },
      // 任务复测权限
      {
        key: 'retest',
        label: (
          <div
            className={isRetest ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => {
              if (!isRetest) return
              setRetestVisible({
                visible: true,
                result_id: id,
                task_id: taskId,
              })
            }}
          >
            <span className="text">{t('任务复测')}</span>
          </div>
        ),

        disabled: !isRetest,
      },
      // 删除权限
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'download-btn' : 'download-btn-disabled'}
            onClick={() => {
              if (!isRemove) return
              removeData(id, 'sub')
            }}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),

        disabled: !isRemove,
      },
    ]

    return <Menu items={items} />
  }

  const columns = [
    {
      title: t('任务名称'),
      dataIndex: 'name',
      key: 'name',
      width: '11%',
      ellipsis: true,
      render: (_, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={record.name} getPopupContainer={() => document.body}>
            <span
              style={{
                maxWidth: 'calc(100% - 24px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </span>
          </Tooltip>
          {record.devices?.length > 0 && (
            <Tooltip
              getPopupContainer={() => document.body}
              overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
              title={
                <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                  <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                  {record.devices.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 5,
                        flexWrap: 'nowrap',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '1 1 auto',
                          minWidth: 0,
                        }}
                      >
                        {item.deviceType} | {item.deviceName} | {item.deviceIp}
                      </span>
                      <Badge
                        style={{ margin: '0 8px', flexShrink: 0 }}
                        status={item.isOnline ? 'success' : 'default'}
                      />

                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {item.isOnline ? t('在线') : t('离线')}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              <SvgIcon
                name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('任务综合评分'),
      dataIndex: 'score',
      key: 'score',
      width: '15%',
      sorter: (a, b) => a.score - b.score,
      render: ({
        score,
        riskLevel,
        className,
      }: {
        score: number
        riskLevel: string
        className: string
      }) => (
        <span>
          <span className="score">{score || 0}</span>
          <span className="divider">|</span>
          <span className={className}>{riskLevel || '-'}</span>
        </span>
      ),
    },
    {
      title: t('运行状态分布'),
      dataIndex: 'statusList',
      key: 'statusList',
      width: '22%',
      render: statusList => (
        <div style={{ display: 'flex' }}>
          {statusList?.map(status => (
            <div key={status.name} className={`status ${status.className}`}>
              <span>{status.name}</span>
              <span className="count">{status.count}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('执行主体'),
      dataIndex: 'agent',
      key: 'agent',
      width: '18%',
      ellipsis: true,
      render: (_, record) => (
        <Tooltip
          title={`${record?.agentInfo?.agentName}-${record?.agentInfo?.agentIp}`}
          getPopupContainer={() => document.body}
        >
          <span>{record?.agentInfo?.agentName}</span>
        </Tooltip>
      ),
    },
    {
      title: t('执行类型'),
      dataIndex: 'kind',
      key: 'kind',
      width: '10%',
      render: (kind: string, { executeTimesUsed, executeTimesLimit }: ESDataType) => (
        <div
          className={`kind-wrapper${
            [t('立即'), t('预定')].includes(kind) ? ' kind-immediately' : ''
          }`}
        >
          <span className="kind">{kind}</span>
          {![t('立即'), t('预定')].includes(kind) && (
            <>
              <span>{executeTimesUsed || 0}</span>
              <span className="divider">|</span>
              <span>{executeTimesLimit === 0 ? '∞' : executeTimesLimit}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('优先级'),
      dataIndex: 'priority',
      key: 'priority',
      width: '7%',
      ellipsis: true,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: t('分组'),
      dataIndex: 'groupId',
      key: 'groupId',
      width: '10%',
      filteredValue: searchParams?.groupId ? searchParams?.groupId : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('请选择分组')}
            value={props.selectedKeys as string[]}
            open={groupSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setGroupSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
              setSearchParams(prev => ({ ...prev, groupId: value }))
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                      setSearchParams(prev => ({ ...prev, groupId: undefined }))
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setGroupSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={groupList.map(type => ({
              label: type.name,
              value: type.id,
            }))}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (_, record) => <span>{record.groupName || '-'}</span>,
    },
    {
      title: t('首次运行时间'),
      dataIndex: 'firstExecuteTime',
      key: 'firstExecuteTime',
      width: '12%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'date')}
        </Tooltip>
      ),
    },
    {
      title: t('最近结束时间'),
      dataIndex: 'latestExecuteTime',
      key: 'latestExecuteTime',
      width: '12%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'date')}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      fixed: 'right',
      width: '25%',
      render: (_, { id, verificationType, taskStatus, name, exceptReason, isRemove }) => (
        <Space size="middle">
          {taskStatus && (
            <div className="stop-btn">
              {[t('等待中'), t('中断'), t('暂停'), t('错误')].includes(taskStatus) && (
                <span
                  onClick={() => {
                    if (taskStatus === t('中断') || taskStatus === t('错误')) {
                      message.warn({
                        className: 'task-warn',
                        content: (
                          <div>
                            <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                            {t('异常原因：')}
                            {exceptReason}
                          </div>
                        ),
                      })
                      return
                    }
                    handleOpenTask(id, 2)
                  }}
                >
                  <span className="text">{t('开启')}</span>
                </span>
              )}
              {[t('进行中')].includes(taskStatus) && (
                <span onClick={() => handleOpenTask(id, 1)}>
                  <span className="text">{t('暂停')}</span>
                </span>
              )}
            </div>
          )}
          <div className="view-btn" onClick={() => viewData(id)}>
            <span className="text">{t('查看')}</span>
          </div>
          <Dropdown
            overlay={buildItems(verificationType, id, name, isRemove)}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 9999 }}
          >
            <span>
              {t('更多')} <DownOutlined />
            </span>
          </Dropdown>
        </Space>
      ),
    },
  ]

  const subColumns = useMemo(() => {
    const arr = [
      {
        title: t('子任务名称'),
        dataIndex: 'taskName',
        key: 'taskName',
        width: '15%',
        ellipsis: true,
        render: (_, record) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title={record.name} getPopupContainer={() => document.body}>
              <span
                style={{
                  maxWidth: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.taskName}
              </span>
            </Tooltip>
            {record.devices?.length > 0 && (
              <Tooltip
                getPopupContainer={() => document.body}
                overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                title={
                  <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                    <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                    {record.devices.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 5,
                          flexWrap: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {item.deviceType} | {item.deviceName} | {item.deviceIp}
                        </span>
                        <Badge
                          style={{ margin: '0 8px', flexShrink: 0 }}
                          status={item.isOnline ? 'success' : 'default'}
                        />

                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {item.isOnline ? t('在线') : t('离线')}
                        </span>
                      </div>
                    ))}
                  </div>
                }
              >
                <SvgIcon
                  name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: t('任务评分'),
        dataIndex: 'score',
        key: 'score',
        width: '10%',
        render: ({
          score,
          riskLevel,
          className,
          status,
        }: {
          score: number | string
          riskLevel: string
          className: string
          status: string
        }) => (
          <span>
            <span className="score">
              {status === 'TaskResultStatusInterrupted'
                ? '-'
                : score === '-'
                ? '-'
                : Number(score) / 100}
            </span>
            <span className="divider">|</span>
            <span className={className}>{riskLevel}</span>
          </span>
        ),
      },
      {
        title: t('运行状态'),
        dataIndex: 'status',
        key: 'status',
        width: '15%',
        render: (status, { caseSendTotal, caseTotal, exceptReason }) => (
          <div className="status-wrapper">
            <div>
              <span>{caseSendTotal || 0}</span>
              <span className="divider">|</span>
              <span>{caseTotal}</span>
            </div>
            <span className={`status ${status?.className}`}>{status?.label}</span>
            {status?.label === t('中断') && (
              <Tooltip title={exceptReason} getPopupContainer={() => document.body}>
                <IconFont
                  type={'icon-jinggao_jinggao'}
                  style={{ color: '#F53F3F', fontSize: '14px' }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: t('创建时间'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: '15%',
        ellipsis: true,
        render: text => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('开始时间'),
        dataIndex: 'startTime',
        key: 'startTime',
        width: '15%',
        ellipsis: true,
        render: text => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('结束时间'),
        dataIndex: 'endTime',
        key: 'endTime',
        width: '15%',
        ellipsis: true,
        render: text => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('操作'),
        key: 'action',
        fixed: 'right',
        width: '25%',
        render: (
          _,
          { taskName, taskId, id, status, isStop, isView, isRetest, isReport, isRemove },
        ) => (
          <Space size="middle">
            {isStop && (
              <div onClick={() => handleStopTask(id, taskId)} className="stop-btn">
                <span style={{ marginLeft: 6 }}>{t('停止')}</span>
              </div>
            )}
            <div
              className={isView ? 'view-btn' : 'view-btn-disabled'}
              onClick={() =>
                isView &&
                window.open(
                  `/validation/network/webgateway?tab=task&name=detail&id=${id}&task_id=${taskId}`,
                  '_blank',
                )
              }
            >
              <span className="text">{t('查看')}</span>
            </div>
            <Dropdown
              overlay={buildSubItems(
                taskName,
                id,
                taskId,
                status?.label,
                isRetest,
                isReport,
                isRemove,
              )}
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 9999 }}
            >
              <span>
                {t('更多')} <DownOutlined />
              </span>
            </Dropdown>
          </Space>
        ),
      },
    ]

    return arr
  }, [subDataSource])

  // 使用自定义Hook管理事件处理
  const useTaskListHandlers = () => {
    const viewData = (id: string) => setView({ visible: true, id })
    const closeView = () => setView({ visible: false, id: '' })

    const removeData = (id: string | string[], type: string) => {
      if (typeof id === 'string') {
        setRemove({ visible: true, id: [id], type })
      } else {
        setRemove({ visible: true, id, type })
      }
    }

    const copyData = (id: string) => {
      navigate('/validation/network/webgateway?tab=create', {
        state: { id },
      })
    }

    const reportData = (id: string, name: string) => {
      console.log('report data', id)
      const taskList = [{ task_name: name, campaign_id: id }]
      setReport({ visible: true, id, name, taskList })
    }

    const closeReport = () => setReport({ visible: false, id: '', name: '' })

    const handleStopTask = async (id: string, taskId: string) => {
      try {
        await stopTask({ result_id: id })
        initTableData(taskId, 1, true)
        Message.success({ content: t('任务停止成功'), className: 'task-success' })
      } catch (error) {
        console.log(error)
      }
    }

    const handleOpenTask = async (id: string, taskStatus: number) => {
      try {
        await changeJobStatus({ id: id, task_status: taskStatus })
        if (taskStatus === 1) {
          Message.error({ content: t('任务暂停'), className: 'task-error' })
        } else {
          Message.info({ content: t('任务启动'), className: 'task-info' })
        }
        loadTableData(id)
      } catch (error: any) {
        if (error.code === 400) {
          message.warn({
            className: 'task-warn',
            content: (
              <span>
                <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                <div>
                  {t('异常原因：')}
                  {error.msg}
                </div>
              </span>
            ),
          })
        }
      }
    }

    const handleDelete = async () => {
      try {
        if (remove.type === 'main') {
          await deleteTask(remove.id)
        } else if (remove.type === 'sub') {
          await deleteSubTask({ result_id_list: remove.id })
        } else {
          await deleteTask(selectedRowKeys)
          if (selectedSubRowKeys.length > 0) {
            await deleteSubTask({ result_id_list: selectedSubRowKeys })
          }
        }
        setRemove({ visible: false, id: [], type: '' })
        setSelectedRowKeys([])
        setSelectedSubRowKeys([])
        setSearchParams({ ...searchParams, page: 1, limit: 10 })
      } catch (error) {
        console.log(error)
      }
    }

    const handleExpand = ({ expanded, record }) => {
      if (expanded) {
        loadTableData(record.id)
        setMainTaskName(record.name)
        setExpandedRowKeys([record.key])
        setCount(1)
      } else {
        setExpandedRowKeys([])
        setSubDataSource([])
        setCount(1)
      }
    }

    const handleReset = () => setSearchParams({ page: 1, limit: 10 })

    const confirmAddGroup = values => {
      let taskData: { task_name: string; campaign_id: string }[] = []
      if (report.multiple) {
        taskData = values.campaign_ids.reduce((acc, id) => {
          const item = report.selectItem.find(i => i.id === id)
          if (item) {
            acc.push({ task_name: item.task_name, campaign_id: item.id })
          }
          return acc
        }, [])
      } else {
        if (report.taskList) {
          taskData = report.taskList.map(item => ({
            ...item,
            campaign_id: String(item.campaign_id),
          }))
        }
      }

      createReport({
        task_list: taskData,
        report_name: values.report_name,
        template_type:
          report?.multiple && report?.selectItem?.length > 1 ? [2] : values.template_type,
        campaign_type: values.campaign_type,
        emails: '',
        assets: 0,
        include_device_logs: values.include_device_logs,
      })
        .then(() => {
          window.open('/report/task/task-report?tab=2', '_self')
        })
        .catch(_ => {})
    }

    return {
      viewData,
      closeView,
      removeData,
      copyData,
      reportData,
      closeReport,
      handleStopTask,
      handleOpenTask,
      handleDelete,
      handleExpand,
      handleReset,
      confirmAddGroup,
    }
  }

  const {
    viewData,
    closeView,
    removeData,
    copyData,
    reportData,
    closeReport,
    handleStopTask,
    handleOpenTask,
    handleDelete,
    handleExpand,
    handleReset,
    confirmAddGroup,
  } = useTaskListHandlers()

  // 计算主任务评分
  const calculateMainTaskScore = (itemScore: number, isUnique: boolean): number | string => {
    if (itemScore === 0) {
      return isUnique ? '-' : 0
    }
    if (itemScore === 10000) return 100
    return Number((itemScore / 100).toFixed(2)) || 0
  }

  // 计算主任务风险等级
  const calculateMainTaskRiskLevel = (score: number | string, isUnique: boolean) => {
    if (isUnique || typeof score === 'string') {
      return { riskLevel: '-', className: '' }
    }

    if (score <= 100 && score >= 80) return { riskLevel: t('优秀'), className: 'low-risk' }
    if (score < 80 && score >= 60) return { riskLevel: t('良好'), className: 'medium-risk' }
    if (score < 60 && score >= 40) return { riskLevel: t('一般'), className: 'high-risk' }
    if (score < 40 && score >= 20) return { riskLevel: t('较弱'), className: 'weaker-risk' }
    return { riskLevel: t('弱'), className: 'serious-risk' }
  }

  // 处理主任务状态列表
  const processStatusList = (statusList: any[]) =>
    statusList.map(status => ({
      name: statusResultMap[status?.status]?.label,
      count: status?.count,
      className: statusResultMap[status?.status]?.className,
    }))

  // 检查是否可以删除
  const checkIsRemove = (statusList: any[]) =>
    !statusList.find(status =>
      [
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status.status),
    )

  // 处理单个主任务项
  const processMainTaskItem = (item: any, taskId: string, subRowKeys: string[]) => {
    const {
      firstExecuteTime,
      latestExecuteTime,
      id,
      kind,
      statusList,
      taskStatus,
      verificationType,
      exceptReason,
    } = item

    const isUnique = !statusList.some(status => status.status !== 'TaskResultStatusInterrupted')
    const score = calculateMainTaskScore(item.score, isUnique)
    const { riskLevel, className } = calculateMainTaskRiskLevel(score, isUnique)

    return {
      ...item,
      key: id,
      firstExecuteTime: firstExecuteTime || '-',
      latestExecuteTime: latestExecuteTime || '-',
      kind: KindMap[kind],
      score: { score, riskLevel, className },
      verificationType: {
        label: VerificationTypeMap[verificationType]?.label,
        color: VerificationTypeMap[verificationType]?.color,
      },
      statusList: processStatusList(statusList),
      isRemove: checkIsRemove(statusList),
      taskStatus: kind === 'TaskKindCycle' ? statusMap[taskStatus]?.label : undefined,
      exceptReason: exceptReason || undefined,
      subRowKeys: item.id === taskId ? subRowKeys : item.subRowKeys,
    }
  }

  // 更新子任务行键
  const updateSubRowKeys = (arr: any[], taskId: string, subRowKeys: string[]) =>
    arr.map(item => {
      if (item.id === taskId) {
        item.subRowKeys = subRowKeys
      }
      return item
    })

  // 构建主任务数据
  const buildDataSource = (data, taskId: string, subRowKeys: string[], isOrigin?: boolean) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))

    // 仅是更新 subRowKeys
    if (isOrigin) {
      return updateSubRowKeys(arr, taskId, subRowKeys)
    }

    return arr.map(item => processMainTaskItem(item, taskId, subRowKeys))
  }

  // 初始化表格数据
  const initTableData = async (taskId?: string, page?: number, onlyRefresh?: boolean) => {
    try {
      console.log('taskId', taskId)
      const res = await getTaskList({ ...searchParams })
      setTotal(res.data.total)
      if (res.data.total === 0) return setDataSource([])
      const resp = await getTaskExecuteList(taskId || res.data.list?.[0]?.id, {
        ...searchParams,
        page: onlyRefresh ? 1 : page || count,
      })
      setSubTotal(resp.data.total)
      if (!mainTaskName) {
        setMainTaskName(res.data.list?.[0]?.name)
      }
      setSubDataSource(buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id))
      const subRowKeys = buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, taskId || res.data.list?.[0]?.id, subRowKeys))

      if (onlyRefresh) return
      if (resp.data.list?.length > 0) {
        setExpandedRowKeys([taskId || res.data.list?.[0]?.id])
      } else {
        setExpandedRowKeys([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  // 加载表格数据
  const loadTableData = async (id: string) => {
    try {
      const res = await getTaskList({ ...searchParams })
      setTotal(res.data.total)
      let resp
      if (count !== 1) {
        resp = await getTaskExecuteList(id, { ...searchParams, page: 1 })
      } else {
        resp = await getTaskExecuteList(id, { ...searchParams, page: count })
      }
      setSubTotal(resp.data.total)
      setSubDataSource(buildSubDataSource(resp.data.list, id))
      const subRowKeys = buildSubDataSource(resp.data.list, id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, id, subRowKeys))
      setExpandedRowKeys([id])
    } catch (error) {
      console.log(error)
    }
  }

  // 加载更多子任务数据
  const loadMoreSubData = async (id: string) => {
    setCount(origin => origin + 1)
    const resp = await getTaskExecuteList(id, { ...searchParams, page: count + 1 })
    setSubDataSource(origin => [...origin, ...buildSubDataSource(resp.data.list, id)])
    const subRowKeys = buildSubDataSource(resp.data.list, id)
      .filter(item => item.isRemove)
      .map(v => v.id)
    setDataSource(origin => [...buildDataSource(origin, id, subRowKeys, true)])
  }

  useEffect(() => {
    setCount(1)
    initTableData(undefined, 1)

    return () => {
      setTotal(0)
      setDataSource([])
      setSubDataSource([])
    }
  }, [searchParams])

  useEffect(() => {
    if (taskFormValue?.field) {
      const selectedOption = options.find(opt => opt.value === taskFormValue.field)
      if (selectedOption) {
        setSearchParams(prev => {
          // 特殊处理 agentId 字段：需要比较实际存储的 agentInfo.agentId 字段
          if (taskFormValue.field === 'agentId') {
            // 检查当前值和新值是否一致
            const currentAgentId = prev['agentInfo.agentId']
            const isEmpty =
              taskFormValue.value === '' ||
              taskFormValue.value === null ||
              taskFormValue.value === undefined ||
              (Array.isArray(taskFormValue.value) && taskFormValue.value.length === 0)

            // 如果当前没有值且新值也为空，不需要更新
            if (!currentAgentId && isEmpty) {
              return prev
            }

            // 如果新值不为空，检查是否与当前值一致
            if (!isEmpty && typeof taskFormValue.value === 'string') {
              const parts = taskFormValue.value.split('&&')
              const newAgentId = parts[0]
              if (currentAgentId === newAgentId) {
                return prev
              }
            }
          } else {
            // 其他字段的常规比较
            if (prev[taskFormValue.field] === taskFormValue.value) {
              return prev
            }
          }

          // 检查值是否为空（空字符串、null、undefined 或空数组）
          const isEmpty =
            taskFormValue.value === '' ||
            taskFormValue.value === null ||
            taskFormValue.value === undefined ||
            (Array.isArray(taskFormValue.value) && taskFormValue.value.length === 0)

          // 获取所有 FilterFormSelect 的字段名
          const filterFormFields = options.map(opt => opt.value)

          // 创建新的参数对象，移除所有 FilterFormSelect 字段和 agentInfo 相关字段
          const newParams = Object.keys(prev).reduce((acc, key) => {
            // 保留非 FilterFormSelect 字段（如 groupId, sortField, sortOrder 等）
            // 同时排除 agentInfo 相关字段，因为这些字段由 agentId 统一管理
            if (
              !filterFormFields.includes(key) &&
              key !== 'agentInfo.agentId' &&
              key !== 'agentInfo.agentName' &&
              key !== 'agentInfo.agentIp'
            ) {
              acc[key] = prev[key]
            }
            return acc
          }, {})

          // 如果值为空，只清除 FilterFormSelect 相关字段，保留其他筛选条件
          if (isEmpty) {
            // agentId 字段的清空已经在 newParams 的构建过程中完成
            return {
              ...newParams,
              page: 1,
              limit: prev.limit,
            }
          }

          // 特殊处理 agentId 字段：解析为 agentInfo.agentId、agentInfo.agentName、agentInfo.agentIp
          if (taskFormValue.field === 'agentId' && typeof taskFormValue.value === 'string') {
            // 格式：1000000000000020658-默认安全域-10.255.200.112,192.168.122.1
            const parts = taskFormValue.value.split('&&')
            const agentId = parts[0]
            const agentName = parts[1]
            const agentIp = parts.slice(2).join('&&') // 处理 IP 中可能包含 - 的情况

            return {
              ...newParams,
              page: 1,
              limit: prev.limit,
              'agentInfo.agentId': agentId,
              'agentInfo.agentName': agentName,
              'agentInfo.agentIp': agentIp,
            }
          }

          // 有值时更新筛选参数
          return {
            ...newParams,
            page: 1,
            limit: prev.limit,
            [taskFormValue.field]: taskFormValue.value,
          }
        })
      }
    }
  }, [taskFormValue])

  useEffect(() => {
    getArea().catch(_ => {})
    getGroupList({ page: 1, limit: 10000 }).catch(_ => {})
    getSecurityDeviceList().catch(_ => {})
    getAgentList().catch(_ => {})
  }, [])

  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    console.log('sorter', sorter)
    try {
      const params = {
        ...searchParams,
        page: pagination.current,
        limit: pagination.pageSize,
      }

      // 处理主表格筛选
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] && filters[key].length > 0) {
            params[key] = filters[key]
          } else {
            delete params[key]
          }
        })
      }

      // 处理主表格排序
      if (sorter && sorter.field) {
        params.sortField =
          sorter.field === 'score'
            ? 'sort_field_score'
            : sorter.field === 'firstExecuteTime'
            ? 'sort_field_update_time'
            : 'sort_field_priority'
        params.sortOrder =
          sorter.order === 'ascend'
            ? 'SORT_ORDER_ASCENDING'
            : sorter.order === 'descend'
            ? 'SORT_ORDER_DESCENDING'
            : undefined
      } else {
        delete params.sortField
        delete params.sortOrder
      }
      if (params.sortField && !params.sortOrder) {
        delete params.sortField
      }
      setSearchParams(params)
    } finally {
    }
  }

  const batchOperation = (
    <Space split={<Divider type={'vertical'} />}>
      <div>{t('批量操作')}</div>
      <div className="batch-delete-btn" onClick={() => removeData([], 'all')}>
        <SvgIcon name={'batch_remove'} className="icon-delete" />
        <span>{t('批量删除')}</span>
      </div>
      {selectedRowKeys.length > 1 ? (
        <></>
      ) : (
        selectedSubRowKeys.length > 0 && (
          <div
            className={
              selectedSubRowKeys.length > 5 ? 'batch-report-btn disabled' : 'batch-report-btn'
            }
            onClick={() => {
              if (selectedSubRowKeys.length > 5) return
              const selectItem = [
                ...new Map(
                  subDataSource
                    .filter(item => selectedSubRowKeys.includes(item.id))
                    .map(v => [
                      v.id,
                      {
                        ...v,
                        task_name: v.taskName,
                        result_status:
                          v.status?.label === t('已完成')
                            ? 3
                            : v.status?.label === t('中断')
                            ? 4
                            : 7,
                        executed_plugin_num: v.caseSendTotal,
                      },
                    ]),
                ).values(),
              ]

              console.log('selectItem', selectItem)

              setReport({
                visible: true,
                multiple: true,
                name: '',
                id: selectedSubRowKeys,
                selectItem,
              })
            }}
          >
            <SvgIcon
              name={selectedSubRowKeys.length > 0 ? t('批量生成报表') : 'batch_remove_ash'}
              className="icon-export"
            />

            <span>{t('批量生成报表')}</span>
          </div>
        )
      )}
    </Space>
  )

  return (
    <PageContainer>
      <Wrapper>
        {/* 标题 */}
        <div className="title-wrapper">
          <div className="title">
            <span>{t('任务列表')}</span>
          </div>
          {/* 搜素框 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="search-wrapper" style={{ marginRight: 8 }}>
              <FilterFormSelect
                filterOptions={options}
                value={taskFormValue}
                onChange={newValue => {
                  setTaskFormValue(newValue)
                }}
              />
            </div>
            <div className="reset">
              <Button
                icon={<SyncOutlined style={{ color: '#666' }} />}
                className="reset-btn"
                onClick={() => {
                  initTableData(expandedRowKeys[0], 1, true)
                  setCount(1)
                }}
              />
            </div>
          </div>
        </div>
        {/* 任务列表 */}
        <div className="table-wrapper">
          <TaskTable
            batchOperation={batchOperation}
            columns={columns}
            subColumns={subColumns}
            dataSource={dataSource}
            subDataSource={subDataSource}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            total={total}
            loading={getTaskListLoading}
            hasMore={subTotal > searchParams.limit * count}
            loadMoreSubData={loadMoreSubData}
            subLoading={getTaskExecuteListLoading}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedSubRowKeys={selectedSubRowKeys}
            setSelectedSubRowKeys={setSelectedSubRowKeys}
            expandedRowKeys={expandedRowKeys}
            expandHandler={handleExpand}
            onTableChange={handleTableChange}
          />
        </div>
        {/* 详情抽屉 */}
        <DetailDrawer open={view.visible} onClose={closeView} id={view.id} />
        {/* 删除任务弹窗 */}
        <DeleteModal
          text={
            remove.type === 'main'
              ? t('是否确认删除所选任务（删除主任务时将同时删除其下的子任务）？')
              : t('此操作将永久删除，是否继续？')
          }
          open={remove.visible}
          onOk={() => handleDelete()}
          onCancel={() => setRemove(origin => ({ ...origin, visible: false, type: '' }))}
          loading={
            (remove.type === 'main' && deleteTaskLoading) ||
            (remove.type === 'sub' && deleteSubTaskLoading)
          }
        />

        {/* 创建报表弹窗 */}
        <CreateReport
          visible={report.visible}
          multiple={report.multiple}
          selectItem={report.selectItem}
          createLoading={createLoading}
          templateType={2}
          onConfirm={e => {
            confirmAddGroup(e)
          }}
          onCancel={closeReport}
        />

        <RetestModal
          visible={retestVisible.visible}
          onCancel={() => {
            setRetestVisible({ visible: false, result_id: '', task_id: '' })
          }}
          onSuccess={() => {
            setRetestVisible(origin => ({ ...origin, visible: false }))
            initTableData(retestVisible.task_id, 1)
            setCount(1)
          }}
          id={retestVisible.result_id}
          name={mainTaskName}
        />
      </Wrapper>
    </PageContainer>
  )
}

export default TaskListMain

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 6px;

    .title {
      font-weight: 600;
      font-size: 16px;
    }

    .reset-btn {
      width: 32px;
      height: 32px;
      background: #f1f3f8;
      border-radius: 6px;
      border: none;

      &:hover {
        background: #e6e9f0;
        /* border: 1px solid #4238c9; */
      }
    }
  }

  .table-wrapper {
    padding: 0 20px;
    overflow-y: scroll;
  }
`
const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
